import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useForm from 'components/hooks/useForm';
import useRequestError from 'components/hooks/useRequestError';

import Alert from 'components/application/Alert';
import Portal from 'components/application/Portal';
import RequestErrorMessage from 'components/application/RequestErrorMessage';

import EnableFormCard from 'components/settings/singleSignOn/components/EnableFormCard';
import EnforceFormCard from 'components/settings/singleSignOn/components/EnforceFormCard';

import { pick } from 'components/helpers/objects';
import { scrollToTop } from 'components/helpers/scrolling';

const initialSamlProviderConfiguration = {
  enabled: false,
  enforced: false,
  loaded: false,
};

const initialFormValues = {
  enabled: false,
  providerName: '',
  targetUrl: '',
  certificate: '',
  enforced: false,
};

export default function SingleSignOnConfiguration({
  assertionConsumerServiceUrl,
  domainNames,
  isSamlSsoEnforceable,
  providerNames,
  samlProviderConfigurationId,
  serviceProviderMetadataUrl,
}) {
  const bodyRef = useRef(document.body);

  const [isRequestSuccess, setIsRequestSuccess] = useState(false);

  const [samlProviderConfiguration, setSamlProviderConfiguration] = useState(
    initialSamlProviderConfiguration,
  );

  const [formValues, setFormValues, handleInputChange] =
    useForm(initialFormValues);

  const [
    requestError,
    ,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.singleSignOn.components.SingleSignOnConfiguration',
  });

  const resetAlerts = () => {
    resetRequestError();
    setIsRequestSuccess(false);
  };

  const onNameChange = (selected) => {
    const formValuesSource =
      selected.value === samlProviderConfiguration.providerName ?
        samlProviderConfiguration
      : initialFormValues;
    const newFormValues = pick(formValuesSource, 'targetUrl', 'certificate');

    setFormValues((prevState) => ({
      ...prevState,
      ...newFormValues,
      providerName: selected.value,
    }));
  };

  const onSuccess = () => {
    setIsRequestSuccess(true);
    scrollToTop(bodyRef);
  };

  const onError = (error) => {
    handleRequestError(error);
    scrollToTop(bodyRef);
  };

  const refreshSamlProviderConfiguration = (attributes) => {
    setSamlProviderConfiguration({ ...attributes, loaded: true });
  };

  const refreshForm = (attributes, ...keys) => {
    const newFormValues = pick(attributes, ...keys);

    setFormValues((prevState) => ({ ...prevState, ...newFormValues }));
  };

  const onEnableFormSubmit = () => {
    resetAlerts();

    const params = {
      saml_provider_configuration: {
        enabled: formValues.enabled,
        provider_name: formValues.providerName,
        target_url: formValues.targetUrl,
        certificate: formValues.certificate,
      },
    };

    axios
      .patch(
        `/saml_provider_configurations/${samlProviderConfigurationId}`,
        params,
      )
      .then((response) => {
        const { attributes } = response.data.data;

        refreshForm(
          attributes,
          'enabled',
          'providerName',
          'targetUrl',
          'certificate',
        );
        refreshSamlProviderConfiguration(attributes);
        onSuccess();
      })
      .catch(onError);
  };

  const onEnforceFormSubmit = () => {
    resetAlerts();

    const params = {
      saml_provider_configuration: { enforced: formValues.enforced },
    };

    axios
      .patch(
        `/saml_provider_configurations/${samlProviderConfigurationId}`,
        params,
      )
      .then((response) => {
        const { attributes } = response.data.data;

        refreshForm(attributes, 'enforced');
        refreshSamlProviderConfiguration(attributes);
        onSuccess();
      })
      .catch(onError);
  };

  const fetchSamlProviderConfiguration = () => {
    axios
      .get(`/saml_provider_configurations/${samlProviderConfigurationId}`)
      .then((response) => {
        const { attributes } = response.data.data;

        refreshForm(
          attributes,
          'enabled',
          'providerName',
          'targetUrl',
          'certificate',
          'enforced',
        );
        refreshSamlProviderConfiguration(attributes);
      })
      .catch(onError);
  };

  useEffect(() => {
    fetchSamlProviderConfiguration();
  }, []);

  return (
    <>
      {samlProviderConfiguration.loaded ?
        <>
          <EnableFormCard
            assertionConsumerServiceUrl={assertionConsumerServiceUrl}
            domainNames={domainNames}
            formValues={formValues}
            isEnforced={samlProviderConfiguration.enforced}
            onInputChange={handleInputChange}
            onNameChange={onNameChange}
            onSubmit={onEnableFormSubmit}
            providerNames={providerNames}
            removeErrorStyling={removeErrorStyling}
            requestError={requestError}
            serviceProviderMetadataUrl={serviceProviderMetadataUrl}
          />
          <div className='tw-mb-6' />
          <EnforceFormCard
            formValues={formValues}
            isEnabled={samlProviderConfiguration.enabled}
            isUpgradeRequired={!isSamlSsoEnforceable}
            onInputChange={handleInputChange}
            onSubmit={onEnforceFormSubmit}
          />
        </>
      : null}
      <Portal containerSelector='#single-sign-on-page-alert'>
        <>
          {isRequestSuccess && (
            <div className='tw-mb-6'>
              <Alert type='success'>{i18nT('success')}</Alert>
            </div>
          )}
          {(requestError.isFallback ||
            Object.keys(requestError.validationErrors).length > 0) && (
            <div className='tw-mb-6'>
              <Alert type='danger'>
                <RequestErrorMessage
                  isFallback={requestError.isFallback}
                  validationErrors={requestError.validationErrors}
                />
              </Alert>
            </div>
          )}
        </>
      </Portal>
    </>
  );
}

SingleSignOnConfiguration.propTypes = {
  isSamlSsoEnforceable: PropTypes.bool.isRequired,
  domainNames: PropTypes.string.isRequired,
  serviceProviderMetadataUrl: PropTypes.string.isRequired,
  assertionConsumerServiceUrl: PropTypes.string.isRequired,
  samlProviderConfigurationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  providerNames: PropTypes.arrayOf(PropTypes.string).isRequired,
};
